import { Calendar, ChevronDown, ChevronRight, Edit2, Plus, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import { useState } from "react";
import LogForm from "../LogForm";

const GoalTreeNode = ({ goal, allGoals, getSubgoals, onLog, onEdit, onDelete, onView, onManualScale, onAddSubgoal, level }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const subgoals = getSubgoals(goal._id);
  const hasSubgoals = subgoals.length > 0;
  const progress = (goal.progress / goal.target) * 100;
  const timeframeLabel = goal.timeframe.charAt(0).toUpperCase() + goal.timeframe.slice(1);

  const indentClass = level === 0 ? '' : 'ml-8 border-l-2 border-indigo-200 pl-4';


  return (
    <div className={indentClass}>
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition">
        <div className="flex items-start gap-3">
          {hasSubgoals && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-1 p-1 hover:bg-gray-200 rounded transition"
            >
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-600" />
              )}
            </button>
          )}
          
          <div className="flex-1">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800">{goal.name}</h3>
                {level > 0 && (
                  <span className="text-xs text-indigo-600 font-medium">Subgoal (Level {level})</span>
                )}
              </div>
              <div className="flex gap-1">
                <button onClick={() => onAddSubgoal(goal._id)} className="p-1 hover:bg-blue-100 rounded" title="Add Subgoal">
                  <Plus className="w-4 h-4 text-blue-600" />
                </button>
                <button onClick={() => onEdit(goal)} className="p-1 hover:bg-gray-200 rounded">
                  <Edit2 className="w-4 h-4 text-gray-600" />
                </button>
                <button onClick={() => onDelete(goal._id)} className="p-1 hover:bg-red-100 rounded">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{goal.progress} / {goal.target} {goal.unit}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full transition-all duration-500"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
              <Calendar className="w-3 h-3" />
              <span>{timeframeLabel}</span>
              <span className="mx-1">•</span>
              <span>Scale: {goal.scalePercent}%</span>
              {goal.goalDirection === 'decrease' && (
                <>
                  <span className="mx-1">•</span>
                  <span className="text-orange-600 font-medium">↓ Decrease Goal</span>
                </>
              )}
              {hasSubgoals && (
                <>
                  <span className="mx-1">•</span>
                  <span className="text-indigo-600 font-medium">{subgoals.length} subgoal{subgoals.length !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>

            <LogForm goal={goal} onLog={onLog} />

            <div className="flex gap-2 mt-2">
              <button
                onClick={() => onManualScale(goal._id, 'up')}
                className="flex-1 flex items-center justify-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs hover:bg-blue-200"
              >
                <TrendingUp className="w-3 h-3" />
                Scale Up
              </button>
              <button
                onClick={() => onManualScale(goal._id, 'down')}
                className="flex-1 flex items-center justify-center gap-1 bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs hover:bg-orange-200"
              >
                <TrendingDown className="w-3 h-3" />
                Scale Down
              </button>
            </div>
          </div>
        </div>
      </div>

      {hasSubgoals && isExpanded && (
        <div className="mt-4 space-y-4">
          {subgoals.map(subgoal => (
            <GoalTreeNode
              key={subgoal._id}
              goal={subgoal}
              allGoals={allGoals}
              getSubgoals={getSubgoals}
              onLog={onLog}
              onEdit={onEdit}
              onDelete={onDelete}
              onView={onView}
              onManualScale={onManualScale}
              onAddSubgoal={onAddSubgoal}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default GoalTreeNode;